import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export interface PageHeadingProps {
  /** Page title — rendered as the `h1` at the `text-h1` role. */
  title: string;
  /**
   * Optional supporting description shown under the title. Uses
   * `text-text-secondary` (NOT `text-text-muted`) — the higher-contrast,
   * readable tone that is the design-system default for page descriptions.
   * Width is capped at `max-w-2xl` so long copy keeps a comfortable line
   * length.
   */
  description?: ReactNode;
  /** Optional leading glyph shown next to the title. */
  icon?: LucideIcon;
  /** Tone utility for the icon. Defaults to `text-accent`. */
  iconClassName?: string;
  /** Wrapper override — use for outer spacing (e.g. `mb-8` before a form). */
  className?: string;
}

/**
 * PageHeading — the canonical detail-page header: an `h1` title plus an
 * optional description.
 *
 * This is the single source of truth for the title + description pairing
 * so every detail page reads the same way. The description deliberately
 * uses `text-text-secondary` (readable) rather than `text-text-muted`
 * (low-contrast) — the latter was the cause of the AgendaGPT description
 * legibility complaint (Jared, 2026-06-25). New detail pages should reach
 * for this primitive instead of hand-rolling `<h1>` + `<p>`.
 *
 * Tokenized: `text-h1` / `text-body` roles + token color utilities only.
 * No inline styles, no arbitrary `text-[Npx]` (design-system hard rules
 * #2 / #3). Consumes only React + lucide types — zero internal deps
 * (hard rule #1).
 */
export function PageHeading({
  title,
  description,
  icon: Icon,
  iconClassName = "text-accent",
  className,
}: PageHeadingProps) {
  return (
    <div className={className}>
      <div className={Icon ? "mb-2 flex items-center gap-3" : "mb-2"}>
        {Icon ? (
          <Icon
            className={`w-6 h-6 shrink-0 ${iconClassName}`}
            aria-hidden="true"
          />
        ) : null}
        <h1 className="text-h1 text-text">{title}</h1>
      </div>
      {description ? (
        <p className="text-body text-text-secondary max-w-2xl">{description}</p>
      ) : null}
    </div>
  );
}
